// Ch83 · make the record true after a send that actually left.
//
// Found reviewing #540. The composer replaced a flow that DID update the
// record: the old "Log Text" button inserted an `activities` row of type
// `sent_text`, and migration 031's trg_bump_client_last_contacted_on_activity
// bumps `clients.last_contacted_at` from it. The work list reads that column —
// `client?.last_contacted_at ? "contacted_waiting" : "converted_uncontacted"`
// — so before this, a client you had just texted stayed in "Converted, no
// contact yet" and the row kept telling you to text them.
//
// That is a regression my own PR introduced, not a pre-existing gap: seven
// steps that ended in a correct record beat one click that ends in a wrong one.
//
// WHY THE FRONTEND, when the send route is the better long-term home: the
// route knows it succeeded and would fix this for every caller. But the route
// lives behind a Render deploy while the button ships with Vercel, so a
// backend fix would land on a different clock than the button that needs it —
// and the insert here is byte-for-byte the one LogActivityDialog already does.
// Moving it server-side is worth doing; it is not worth a window where the
// button is live and the record is wrong.
//
// LEADS ARE NOT COVERED, and the reason is structural rather than an
// oversight: the trigger updates `clients` keyed on the activity's entity_id,
// a texted lead has no client row to update, and `leads` has no
// last_contacted_at column at all. A lead's stage comes from the claim ledger,
// so texting one does not move it. Closing that needs either a migration or a
// board that reads sms_messages — a design decision, not a patch.
import { supabase } from "./supabase";

/**
 * Best effort by design. The text HAS left the building by the time this runs;
 * failing the operator's send because a bookkeeping row did not insert would
 * trade a real outcome for a tidy one. Mirrors the asymmetry sms.py already
 * applies to carrier opt-out writes.
 *
 * @returns true when the activity row landed, false otherwise. Callers use it
 *   for telemetry or tests, never to decide whether the text was sent.
 */
export async function recordSentText(
  clientId: string,
  clientName: string,
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { error } = await supabase.from("activities").insert({
      user_id: user.id,
      activity_type: "sent_text",
      entity_type: "client",
      entity_id: clientId,
      metadata: { client_name: clientName, notes: "Sent from the Leads work list" },
    });
    return !error;
  } catch {
    return false;
  }
}
